import { useRef } from "react"
import { useRefresh } from "lib/@hooks/useRefresh"
import { ensureArray } from "lib/ensure-array"
import { IS_LOADING } from "lib/graphql/return-is-loading-on-each-request"
import { withInitialValue } from "lib/graphql/with-initial-value"
import { getAuthoringLegislationLibrary } from "./controller/get-authoring-legislation-library"
import { getAuthoringLegislationList } from "./controller/get-authoring-legislation-list"
import { getAuthoringLegislationGroup } from "./controller/get-authoring-legislation-group"
import {
    AuthoringLegislationUpdated,
    AuthoringLegislationDeleted,
    AuthoringLegislationGroupUpdated,
    AuthoringLegislationGroupDeleted,
    AuthoringLegislationGroupMembershipAdded,
    AuthoringLegislationGroupMembershipRemoved,
} from "./server/legislation-library-event-definitions.mjs"
import {
    buildLegislationGraph,
    getChildren,
    getPath,
    getGroupsForLegislation,
    setFolder,
    removeFolder,
    setLegislation,
    removeLegislation,
    placeLegislation,
    unplaceLegislation,
} from "./legislation-library-helpers"

import { findInSubtree } from "./legislation-library-search"

export function useAuthoringLegislationLibrary() {
    const treePayload = getAuthoringLegislationLibrary.useResultsOnce({}, withInitialValue(IS_LOADING))
    const isLoading = treePayload === IS_LOADING
    const refresh = useRefresh()

    const stateRef = useRef(null)
    if (!stateRef.current) {
        stateRef.current = {
            lookup: new Map(),
            rootIds: [],
            refresh,
        }
    }

    const builtRef = useRef(false)
    if (treePayload && treePayload !== IS_LOADING && !builtRef.current) {
        buildLegislationGraph(stateRef.current, treePayload)
        builtRef.current = true
    }

    AuthoringLegislationUpdated.useEvent(handleLegislationUpdated, [])
    AuthoringLegislationDeleted.useEvent(handleLegislationDeleted, [])
    AuthoringLegislationGroupUpdated.useEvent(handleGroupUpdated, [])
    AuthoringLegislationGroupDeleted.useEvent(handleGroupDeleted, [])
    AuthoringLegislationGroupMembershipAdded.useEvent(handleMembershipAdded, [])
    AuthoringLegislationGroupMembershipRemoved.useEvent(handleMembershipRemoved, [])

    const state = stateRef.current

    return {
        isLoading,
        state,
        getNode: (id) => state.lookup.get(id) ?? null,
        getChildren: (folderId) => getChildren(state, folderId),
        getRootIds: () => state.rootIds,
        getPath: (id) => getPath(state, id),
        getGroupsForLegislation: (legId) => getGroupsForLegislation(state, legId),
        findInSubtree: (rootId, query) => findInSubtree(state, rootId, query),
        setFolder: (folder) => setFolder(state, folder),
        removeFolder: ({ removedGroupIds }) => removeFolder(state, removedGroupIds),
        setLegislation: (legislation) => setLegislation(state, legislation),
        removeLegislation: (id) => removeLegislation(state, id),
        placeLegislation: (result) => placeLegislation(state, result),
        unplaceLegislation: (legId, groupId) => unplaceLegislation(state, legId, groupId),
        refresh,
    }

    async function handleLegislationUpdated({ ids } = {}) {
        if (!ids) return
        const items = (await getAuthoringLegislationList({ ids: ensureArray(ids) })) ?? []
        for (const item of items) setLegislation(state, item, { silent: true })
        state.refresh()
    }

    function handleLegislationDeleted({ ids } = {}) {
        if (!ids) return
        for (const id of ensureArray(ids)) removeLegislation(state, id)
    }

    async function handleGroupUpdated({ ids } = {}) {
        if (!ids) return
        const fetched = await Promise.all(ensureArray(ids).map((id) => getAuthoringLegislationGroup({ id })))
        applyGroupsInSafeOrder(fetched.filter(Boolean))
    }

    // A batch can contain both a freshly-created parent and its children — Promise.all above
    // doesn't preserve order, so applying naively could attach a child before its parent exists
    // locally. Repeatedly apply whichever group's parent is already available, parents first.
    function applyGroupsInSafeOrder(groups, { silent = false } = {}) {
        const remainingIds = new Set(groups.map((group) => group.id))
        const queue = [...groups]
        while (queue.length) {
            const index = queue.findIndex((group) => !group.parentId || !remainingIds.has(group.parentId))
            if (index === -1) break
            const [group] = queue.splice(index, 1)
            remainingIds.delete(group.id)
            setFolder(state, group, { silent: true })
        }
        for (const group of queue) setFolder(state, group, { silent: true })
        if (!silent) state.refresh()
    }

    function handleGroupDeleted({ ids } = {}) {
        if (!ids) return
        removeFolder(state, ensureArray(ids))
    }

    // A membership event can arrive before the folder/legislation it references, since each
    // syncs independently. If that happens, placeLegislation just does nothing — so fetch what's missing first.
    async function handleMembershipAdded({ items } = {}) {
        const pairs = items ?? []
        if (!pairs.length) return

        const missingLegislationIds = [...new Set(pairs.map((pair) => pair.legislationId))].filter(
            (id) => !state.lookup.has(id)
        )
        const missingFolderIds = [...new Set(pairs.map((pair) => pair.parentId))].filter((id) => !state.lookup.has(id))

        if (missingLegislationIds.length) {
            const fetched = (await getAuthoringLegislationList({ ids: missingLegislationIds })) ?? []
            for (const item of fetched) setLegislation(state, item, { silent: true })
        }
        if (missingFolderIds.length) {
            const fetched = await Promise.all(missingFolderIds.map((id) => getAuthoringLegislationGroup({ id })))
            applyGroupsInSafeOrder(fetched.filter(Boolean), { silent: true })
        }

        for (const { legislationId, parentId } of pairs) {
            placeLegislation(state, { legislationId, parentId }, { silent: true })
        }
        state.refresh()
    }

    function handleMembershipRemoved({ items } = {}) {
        for (const { legislationId, parentId } of items ?? []) {
            unplaceLegislation(state, legislationId, parentId, { silent: true })
        }
        state.refresh()
    }
}
